import clsx from 'clsx';
import {
  Children,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';

import { Button } from '../button/Button';
import styles from './Carousel.module.scss';

export const CAROUSEL_NAVIGATIONS = ['arrows', 'dots', 'both', 'none'] as const;
export const CAROUSEL_SLIDE_SIZES = ['full', 'peek'] as const;
export const CAROUSEL_GAPS = ['sm', 'md', 'lg'] as const;

export const carouselPropsSchema = z
  .object({
    navigation: z.enum(CAROUSEL_NAVIGATIONS).optional(),
    slideSize: z.enum(CAROUSEL_SLIDE_SIZES).optional(),
    gap: z.enum(CAROUSEL_GAPS).optional(),
    'aria-label': z.string().min(1).optional(),
  })
  .strict();

export type CarouselDataProps = z.infer<typeof carouselPropsSchema>;

export interface CarouselProps extends CarouselDataProps {
  className?: string;
  children?: ReactNode;
}

export const Carousel = ({
  navigation = 'arrows',
  slideSize = 'full',
  gap = 'md',
  'aria-label': ariaLabel = 'Carousel',
  className,
  children,
}: CarouselProps) => {
  const slides = useMemo(
    () => Children.toArray(children).filter(Boolean),
    [children]
  );
  const slideCount = slides.length;
  const showNav = slideCount > 1 && navigation !== 'none';
  const showArrows =
    showNav && (navigation === 'arrows' || navigation === 'both');
  const showDots = showNav && (navigation === 'dots' || navigation === 'both');

  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const intersectionRatiosRef = useRef(new Map<Element, number>());
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToSlide = useCallback(
    (index: number) => {
      const slide = slideRefs.current[index];
      if (!slide) {
        return;
      }

      slide.scrollIntoView({
        behavior: 'smooth',
        inline: slideSize === 'peek' ? 'center' : 'start',
        block: 'nearest',
      });
    },
    [slideSize]
  );

  useEffect(() => {
    const track = trackRef.current;
    if (!track || slideCount <= 1) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          intersectionRatiosRef.current.set(
            entry.target,
            entry.intersectionRatio
          );
        });

        let bestIndex = 0;
        let bestRatio = -1;

        slideRefs.current.forEach((slide, index) => {
          if (!slide) {
            return;
          }

          const ratio = intersectionRatiosRef.current.get(slide) ?? 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });

        if (bestRatio > 0) {
          setActiveIndex(bestIndex);
        }
      },
      {
        root: track,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    slideRefs.current.forEach(slide => {
      if (slide) {
        observer.observe(slide);
      }
    });

    return () => observer.disconnect();
  }, [slideCount]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' && activeIndex > 0) {
      event.preventDefault();
      scrollToSlide(activeIndex - 1);
      return;
    }

    if (event.key === 'ArrowRight' && activeIndex < slideCount - 1) {
      event.preventDefault();
      scrollToSlide(activeIndex + 1);
    }
  };

  return (
    <div
      className={clsx(styles.root, className)}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      tabIndex={showNav ? 0 : undefined}
      onKeyDown={showNav ? handleKeyDown : undefined}
      data-testid="carousel"
    >
      <div
        className={clsx(
          styles.carouselBody,
          !showArrows && styles.carouselBodyTrackOnly
        )}
      >
        {showArrows && (
          <Button
            className={clsx(
              styles.navButton,
              activeIndex === 0 && styles.navButtonInactive
            )}
            iconName="ChevronBoldLeft"
            size="small"
            variant="empty"
            onClick={() => {
              if (activeIndex > 0) {
                scrollToSlide(activeIndex - 1);
              }
            }}
            aria-disabled={activeIndex === 0}
            data-testid="carousel-prev"
            aria-label="Previous slide"
          />
        )}
        <div
          ref={trackRef}
          className={clsx(styles.track, styles[`gap-${gap}`])}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              ref={element => {
                slideRefs.current[index] = element;
              }}
              className={clsx(
                styles.slide,
                slideSize === 'peek' ? styles.slidePeek : styles.slideFull
              )}
              aria-label={`Slide ${index + 1} of ${slideCount}`}
            >
              {slide}
            </div>
          ))}
        </div>
        {showArrows && (
          <Button
            className={clsx(
              styles.navButton,
              activeIndex >= slideCount - 1 && styles.navButtonInactive
            )}
            iconName="ChevronBoldRight"
            size="small"
            variant="empty"
            onClick={() => {
              if (activeIndex < slideCount - 1) {
                scrollToSlide(activeIndex + 1);
              }
            }}
            aria-disabled={activeIndex >= slideCount - 1}
            data-testid="carousel-next"
            aria-label="Next slide"
          />
        )}
      </div>
      {showDots && (
        <div
          className={styles.dots}
          role="tablist"
          aria-label="Carousel slides"
        >
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              className={clsx(
                styles.dot,
                index === activeIndex && styles.dotActive
              )}
              onClick={() => scrollToSlide(index)}
              aria-current={index === activeIndex ? 'true' : undefined}
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
